// Baut die Feld-Konfigurationen der sidebar-Flugplanung
import { format, sub, add, getISODay } from 'date-fns';
import regionsmanagerJson from '@/core/flugverfuegbarkeit/regionsmanager-reisekuerzel.json';
import { buildStatusgruppeOptions, buildEinkaufsartOptions } from './einkaufsart-statusgruppe';

// Berechnet den Verkehrstag (ISO-Wochentag) eines Inlandsflugs relativ zum Abreisedatum
function getVerkehrstagOfInlandsflug(reisetermin, inlandsflug) {
  const dayOfInlandsflug = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
  return getISODay(dayOfInlandsflug);
}

// Berechnet das konkrete Datum eines Inlandsflugs relativ zum Abreisedatum
function getDateOfIlandsflug(reisetermin, inlandsflug) {
  const dateOfInlandsfluge = add(new Date(reisetermin.abreisedatum), { days: +inlandsflug.tag - 1 });
  return format(dateOfInlandsfluge, 'dd.MM.yyyy');
}

// Liefert die Einkaufsdaten-Felder
export function buildEinkaufsdaten(reisetermin) {
  const statusgruppe = reisetermin.flugeinkauf?.statusgruppe;
  const einkaufsart = reisetermin.flugeinkauf?.einkaufsart;
  return [
    {
      label: 'Status Gruppe',
      value: statusgruppe,
      valueEdited: statusgruppe,
      renderer: null,
      editable: true,
      editType: 'select',
      hint: null,
      options: buildStatusgruppeOptions(einkaufsart, statusgruppe),
    },
    {
      label: 'Einkaufsart',
      value: einkaufsart,
      valueEdited: einkaufsart,
      renderer: null,
      editable: true,
      editType: 'select',
      options: buildEinkaufsartOptions(statusgruppe, einkaufsart),
    },
    {
      label: 'Mindestabnahme Airline',
      value: reisetermin.flugeinkauf?.mindestabnahmeAirline,
      valueEdited: reisetermin.flugeinkauf?.mindestabnahmeAirline,
      renderer: null,
      editable: true,
      editType: 'number',
    },
    {
      label: ['Optionsfrist', 'Datum'],
      value: [
        reisetermin.flugeinkauf?.optionsfrist,
        reisetermin.flugeinkauf?.optionsfrist
          ? format(
              sub(new Date(reisetermin.abreisedatum), {
                days: reisetermin.flugeinkauf.optionsfrist,
              }),
              'dd.MM.yyyy'
            )
          : '-',
      ],
      valueEdited: [
        reisetermin.flugeinkauf?.optionsfrist,
        reisetermin.flugeinkauf?.optionsfrist
          ? format(
              sub(new Date(reisetermin.abreisedatum), {
                days: reisetermin.flugeinkauf.optionsfrist,
              }),
              'dd.MM.yyyy'
            )
          : '-',
      ],
      renderer: null,
      split: true,
      editable: [true, false],
      editType: ['number', 'number'],
      append: ['Tage', ''],
    },
    {
      label: ['Ticketfrist', 'Datum'],
      value: [
        reisetermin.flugeinkauf?.ticketfrist ?? null,
        reisetermin.flugeinkauf?.ticketfrist
          ? format(
              sub(new Date(reisetermin.abreisedatum), {
                days: reisetermin.flugeinkauf.ticketfrist,
              }),
              'dd.MM.yyyy'
            )
          : '-',
      ],
      valueEdited: [
        reisetermin.flugeinkauf?.ticketfrist ?? null,
        reisetermin.flugeinkauf?.ticketfrist
          ? format(
              sub(new Date(reisetermin.abreisedatum), {
                days: reisetermin.flugeinkauf.ticketfrist,
              }),
              'dd.MM.yyyy'
            )
          : '-',
      ],
      renderer: null,
      split: true,
      editable: [true, false],
      editType: ['number', 'number'],
      append: ['Tage', ''],
    },
  ];
}

// Liefert die Reise- und Flugdaten-Felder
export function buildReiseUndFlugdaten(reisetermin) {
  return [
    {
      label: 'Priorität',
      value: reisetermin.reise.prioritaet,
      renderer: 'prioritaetWidget',
    },
    {
      label: 'RM',
      value: regionsmanagerJson[reisetermin.reise.regionsmanager]?.name ?? '-',
      renderer: null,
    },
    {
      label: 'Pax',
      value: reisetermin,
      renderer: 'paxWidget',
    },
    {
      label: 'Strecke',
      value:
        (reisetermin.metadata.ziellandZielflughaefen?.map(fh => fh.flughafenIataCode).join('/') ?? '') +
        '-' +
        (reisetermin.metadata.ziellandAbflughaefen?.map(fh => fh.flughafenIataCode).join('/') ?? ''),
      renderer: null,
    },
    {
      label: 'Abreise',
      value: reisetermin.abreisedatum ? format(new Date(reisetermin.abreisedatum), 'dd.MM.yyyy') : '-',
      renderer: null,
    },
    {
      label: 'Rückflugdatum',
      value: reisetermin.hotelendUndRueckflugdatum
        ? format(new Date(reisetermin.hotelendUndRueckflugdatum), 'dd.MM.yyyy')
        : '-',
      renderer: null,
    },
    {
      label: 'SKR Flugangebot',
      value: reisetermin.metadata.skrFlugangebot ?? '-',
      renderer: null,
    },
    {
      label: 'Transferszenario Hin/Rück',
      value: null,
      renderer: `<h6 class="my-1">
                ${
                  (reisetermin.metadata.transferszenarioHin?.substring(0, 2) ?? '-') +
                  ' | ' +
                  (reisetermin.metadata.transferszenarioRueck?.substring(0, 2) ?? '-')
                }
                </h6>`,
    },
    {
      label: 'VK kalk. Vornacht DZ p.P.',
      value: `${
        reisetermin.metadata.vkVornachtDzProPerson
          ? String(reisetermin.metadata.vkVornachtDzProPerson)?.replace('.', ',')
          : '0'
      }€ (${
        reisetermin.metadata.vkVornachtDzProPerson
          ? Math.round(reisetermin.metadata.vkVornachtDzProPerson / 10) * 10
          : '0'
      }€)`,
      renderer: null,
    },
    {
      label: ['TH Langstrecke', 'TH Inlandsflug'],
      value: [reisetermin?.thLangstrecke, reisetermin?.thInlandsflug],
      valueEdited: [reisetermin?.thLangstrecke, reisetermin?.thInlandsflug],
      renderer: null,
      editable: [true, true],
      editType: ['number', 'number'],
      split: true,
      append: ['€', '€'],
    },
  ];
}

// Baut das Anzeige-Modell der Inlandsflüge (nach Tag sortiert) für die Detail-Listen
export function buildInlandsfluegeViewModel(reisetermin) {
  if (!reisetermin?.metadata?.inlandsfluege) {
    return [];
  }
  return reisetermin.metadata.inlandsfluege
    .slice()
    .sort((a, b) => (a.tag === b.tag ? 0 : a.tag > b.tag ? 1 : -1))
    .map(inlandsflug => {
      return [
        {
          label: 'Tag / Verkehrstag / Datum',
          value:
            inlandsflug.tag +
            ' / ' +
            getVerkehrstagOfInlandsflug(reisetermin, inlandsflug) +
            ' / ' +
            getDateOfIlandsflug(reisetermin, inlandsflug),
          renderer: null,
          variant: null,
          show: true,
        },
        {
          label: 'Einkauf',
          value: inlandsflug.einkauf,
          renderer: null,
          variant: null,
          show: true,
        },
        {
          label: 'Abflughafen / Zielflughafen',
          value: inlandsflug.abflughafenIataCode + ' / ' + inlandsflug.zielflughafenIataCode,
          renderer: null,
          variant: null,
          show: true,
        },
        {
          label: 'Früh. Abflug',
          value: inlandsflug.fruehesterAbflug?.substring(0, 5) ?? '-',
          renderer: null,
          variant: null,
          show: inlandsflug.minimaleUmsteigezeit == null,
        },
        {
          label: 'Spät. Ankunft',
          value: inlandsflug.spaetesteAnkunft?.substring(0, 5) ?? '-',
          renderer: null,
          variant: null,
          show: inlandsflug.minimaleUmsteigezeit == null,
        },
        {
          label: 'Minimale Umsteigezeit',
          value: inlandsflug.minimaleUmsteigezeit ?? '-',
          renderer: null,
          variant: null,
          show: true,
        },
        {
          label: 'Flugnummer',
          value: inlandsflug.flugnummer,
          valueEdited: inlandsflug.flugnummer,
          renderer: null,
          variant: null,
          show: true,
          editable: true,
          editType: 'input',
        },
        {
          label: 'In Gruppe inkludiert',
          value: inlandsflug.inGruppeInkludiert,
          valueEdited: inlandsflug.inGruppeInkludiert,
          renderer: null,
          variant: null,
          show: true,
          editable: true,
          editType: 'checkbox',
        },
        {
          label: 'Id',
          value: inlandsflug.id,
          show: false,
        },
      ];
    });
}
